import React from 'react';
import PropTypes from 'prop-types';
import { Button, Card } from '../shared';

const Landing = ({ 
  companyName = 'FindBob',
  companyDisplayName,
  heroTitle,
  heroSubtitle, 
  customContent, 
  onGetStarted,
  brandColor = '#3B82F6',
  logo,
  customCTA,
  features,
  testimonials,
  statistics,
  partnerLogos,
  tenantConfig = {}
}) => {
  // Use tenant-specific values or defaults
  const title = heroTitle || 'Connect with Your Future';
  const subtitle = heroSubtitle || `Join ${companyDisplayName || companyName}'s student network to discover internships and full-time opportunities with experienced financial advisors.`;
  const ctaText = customCTA?.text || 'Get Started';
  const ctaLink = customCTA?.link || '/student/signup';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="text-center">
            {/* Tenant Logo */}
            {logo && (
              <img 
                src={logo} 
                alt={companyDisplayName || companyName}
                className="h-16 mx-auto mb-8"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
              <span className="block">{title}</span>
              <span className="block mt-2" style={{ color: brandColor }}>Start Your Career in Finance</span>
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-xl text-gray-600">
              {subtitle}
            </p>
            <div className="mt-10 flex justify-center gap-4">
              <Button
                size="lg"
                variant="primary"
                onClick={onGetStarted || (() => window.location.href = ctaLink)}
                style={{ backgroundColor: brandColor }}
              >
                {ctaText}
              </Button>
              <Button
                size="lg"
                variant="secondary"
                onClick={() => window.location.href = '/student/about'}
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      {tenantConfig.showStatistics && statistics && statistics.length > 0 && (
        <section className="py-12 bg-white border-y border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {statistics.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl font-bold mb-2" style={{ color: brandColor }}>
                    {stat.value}
                  </div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              {features && features.length > 0 ? 'What We Offer' : 'Why Join Our Platform?'}
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Everything you need to launch your career in financial services
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {features && features.length > 0 ? (
              features.map((feature, index) => (
                <Card key={index} padding="lg" className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" 
                       style={{ backgroundColor: `${brandColor}20` }}>
                    <svg
                      className="w-8 h-8"
                      style={{ color: brandColor }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      {/* Generic icon */}
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </Card>
              ))
            ) : (
              // Default features
              <>
            <Card padding="lg" className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <svg
                  className="w-8 h-8 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Real Opportunities
              </h3>
              <p className="text-gray-600">
                Access exclusive internships and full-time positions directly from financial
                advisors
              </p>
            </Card>

            <Card padding="lg" className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Direct Connections</h3>
              <p className="text-gray-600">
                Message advisors directly, ask questions, and build professional relationships
              </p>
            </Card>

            <Card padding="lg" className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <svg
                  className="w-8 h-8 text-purple-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Verified Positions
              </h3>
              <p className="text-gray-600">
                All opportunities are posted by verified financial professionals
              </p>
            </Card>
            </>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {tenantConfig.showTestimonials && testimonials && testimonials.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900">Student Success Stories</h2>
              <p className="mt-4 text-lg text-gray-600">
                Hear from students who launched their careers through our platform
              </p>
            </div>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              {testimonials.map((testimonial, index) => (
                <Card key={index} padding="lg">
                  <p className="text-lg italic text-gray-700 mb-4">"{testimonial.text}"</p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-gray-900">{testimonial.author}</p>
                    <p className="text-sm text-gray-600">{testimonial.school}</p>
                    {testimonial.role && (
                      <p className="text-sm" style={{ color: brandColor }}>{testimonial.role}</p>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Partner Logos Section */}
      {tenantConfig.showPartnerLogos && partnerLogos && partnerLogos.length > 0 && (
        <section className="py-12 bg-white border-y border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-center text-lg font-semibold text-gray-600 mb-8">
              Partner Universities
            </h3>
            <div className="flex flex-wrap justify-center items-center gap-8">
              {partnerLogos.map((partner, index) => (
                <img
                  key={index}
                  src={`/assets/${partner.logo}`}
                  alt={partner.name}
                  className="h-12 opacity-60 hover:opacity-100 transition-opacity"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Custom Content Section */}
      {customContent && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div dangerouslySetInnerHTML={{ __html: customContent }} />
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16" style={{ backgroundColor: brandColor }}>
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl text-white opacity-90 mb-8">
            Join hundreds of students connecting with financial advisors today
          </p>
          <Button
            size="lg"
            variant="secondary"
            onClick={onGetStarted || (() => window.location.href = ctaLink)}
          >
            Create Your Profile
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p>&copy; {new Date().getFullYear()} {companyDisplayName || companyName}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

Landing.propTypes = {
  companyName: PropTypes.string,
  companyDisplayName: PropTypes.string,
  heroTitle: PropTypes.string,
  heroSubtitle: PropTypes.string,
  customContent: PropTypes.string,
  onGetStarted: PropTypes.func,
  brandColor: PropTypes.string,
  logo: PropTypes.string,
  customCTA: PropTypes.shape({
    text: PropTypes.string,
    link: PropTypes.string,
  }),
  features: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
    })
  ),
  testimonials: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      author: PropTypes.string,
      school: PropTypes.string,
      role: PropTypes.string,
    })
  ),
  statistics: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  partnerLogos: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      logo: PropTypes.string,
    })
  ),
  tenantConfig: PropTypes.shape({
    showStatistics: PropTypes.bool,
    showTestimonials: PropTypes.bool,
    showPartnerLogos: PropTypes.bool,
  }),
};

export default Landing;


